/**
 * Forgot Password Page with multi-step flow
 * Step 1: Enter email
 * Step 2: Enter verification code and new password
 * Step 3: Success message
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Card, CardBody, Button, Input, Alert } from '@components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Logo } from '@components/common/Logo';
import { authService } from '../services/authService';
import { TEST_IDS } from '@lib/utils/testIds';

const forgotPasswordSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
});

const resetPasswordSchema = z
  .object({
    code: z
      .string()
      .length(4, 'El código debe tener 4 dígitos')
      .regex(/^\d{4}$/, 'El código debe contener solo números'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'reset' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Get tenant and tenantUrl from environment variables
  const tenant = import.meta.env.VITE_DEFAULT_TENANT || '172';
  const tenantUrl = import.meta.env.VITE_TENANT_URL || window.location.origin;

  // /reset-password (el enlace del correo) redirige acá con el email en la URL.
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') || '';

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: prefilledEmail },
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const forgotPasswordMutation = useMutation({
    mutationFn: async (emailValue: string) => {
      // El BFF resuelve la cuenta del lado del servidor y devuelve un token
      // opaco. Ya no se llama a getUserByEmail: ese endpoint entregaba el
      // objeto completo del usuario —incluido el PasswordHash— a cualquiera
      // que supiera un correo.
      const forgotResult = await authService.forgotPassword(
        emailValue,
        tenant,
        tenantUrl,
        'Cambiar contraseña'
      );

      return { email: emailValue, resetToken: forgotResult?.resetToken ?? '' };
    },
    onSuccess: (data) => {
      // Si el correo no está registrado no viene resetToken, pero se avanza
      // igual al paso del código: interrumpir acá revelaría qué direcciones
      // existen. El reset fallará después con un error genérico.
      setEmail(data.email);
      setResetToken(data.resetToken);
      setStep('reset');
    },
  });

  // Función para reenviar el código
  const handleResendCode = async () => {
    if (!email) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      // Reenviar emite una solicitud nueva: hay que quedarse con su token,
      // porque el anterior queda huérfano.
      const resendResult = await authService.forgotPassword(
        email,
        tenant,
        tenantUrl,
        'Cambiar contraseña'
      );
      setResetToken(resendResult?.resetToken ?? '');
      setResendSuccess(true);
      // Ocultar mensaje de éxito después de 5 segundos
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend code:', error);
    } finally {
      setIsResending(false);
    }
  };

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordFormData) => {
      return authService.resetPassword(
        resetToken,
        data.password,
        data.confirmPassword,
        data.code
      );
    },
    onSuccess: () => {
      setStep('success');
      setTimeout(() => navigate('/login'), 3000);
    },
    onError: (error: any) => {
      console.error('Password reset error:', error?.response?.data?.code ?? error?.message);
    },
  });

  const onSubmitEmail = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(data.email);
  };

  const onSubmitReset = (data: ResetPasswordFormData) => {
    resetPasswordMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 dark:bg-secondary-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo height={80} />
          </div>
          <p className="text-secondary-600 dark:text-secondary-400">
            Portal de Facturación Electrónica
          </p>
        </div>

        <Card>
          <CardBody className="p-8">
            {step === 'email' && (
              <>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                  Recuperar Contraseña
                </h2>
                <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                  Ingresa tu correo electrónico y te enviaremos un código de verificación.
                </p>

                <form onSubmit={handleSubmitEmail(onSubmitEmail)} className="space-y-6" data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.FORM}>
                  {forgotPasswordMutation.error && (
                    <Alert variant="danger" title="Error">
                      {(() => {
                        const error = forgotPasswordMutation.error as any;
                        const errorMessage = error?.response?.data?.message || error?.message;

                        if (errorMessage?.includes('No se encontró el usuario')) {
                          return 'El correo electrónico no está registrado. Verifica que sea correcto.';
                        }
                        if (errorMessage?.includes('Network Error')) {
                          return 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
                        }

                        return 'Ocurrió un error al enviar el código. Por favor, intenta de nuevo.';
                      })()}
                    </Alert>
                  )}

                  <Input
                    label="Correo Electrónico"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    error={errorsEmail.email?.message}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.EMAIL_INPUT}
                    {...registerEmail('email')}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    isLoading={forgotPasswordMutation.isPending}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.SUBMIT_BUTTON}
                  >
                    Enviar Código de Verificación
                  </Button>

                  <Link to="/login">
                    <Button
                      variant="ghost"
                      size="lg"
                      className="w-full"
                      leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
                    >
                      Volver al Login
                    </Button>
                  </Link>
                </form>
              </>
            )}

            {step === 'reset' && (
              <>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                  Crear Nueva Contraseña
                </h2>
                <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                  Ingresa el código que enviamos a tu correo y tu nueva contraseña.
                </p>

                <form onSubmit={handleSubmitReset(onSubmitReset)} className="space-y-6">
                  {resetPasswordMutation.error && (
                    <Alert variant="danger" title="Error al Resetear Contraseña">
                      {(() => {
                        const error = resetPasswordMutation.error as any;
                        const errorMessage = error?.response?.data?.message || error?.message;
                        if (errorMessage) {
                          return `Error: ${errorMessage}`;
                        }
                        return 'El código es incorrecto o ha expirado. Por favor, verifica el código enviado a tu correo o solicita uno nuevo.';
                      })()}
                    </Alert>
                  )}

                  <Alert variant="info" title="Código de Verificación">
                    <p>
                      Hemos enviado un código de 4 dígitos a tu correo electrónico:{' '}
                      <strong>{email}</strong>
                    </p>
                    <p className="text-xs mt-2 text-secondary-500">
                      Si no lo recibes, revisa tu carpeta de spam o{' '}
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={isResending}
                        className="text-primary-600 hover:text-primary-700 underline font-medium disabled:opacity-50"
                      >
                        {isResending ? 'reenviando...' : 'reenviar código'}
                      </button>
                    </p>
                  </Alert>

                  {resendSuccess && (
                    <Alert variant="success" title="Código Reenviado">
                      Se ha reenviado el código de verificación a tu correo.
                    </Alert>
                  )}

                  <Input
                    label="Código de Verificación"
                    type="text"
                    placeholder="1234"
                    maxLength={4}
                    error={errorsReset.code?.message}
                    helperText="Ingresa el código de 4 dígitos enviado a tu correo"
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.CODE_INPUT}
                    {...registerReset('code')}
                  />

                  <Input
                    label="Nueva Contraseña"
                    type="password"
                    placeholder="••••••••"
                    error={errorsReset.password?.message}
                    helperText="Mínimo 8 caracteres"
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.PASSWORD_INPUT}
                    {...registerReset('password')}
                  />

                  <Input
                    label="Confirmar Contraseña"
                    type="password"
                    placeholder="••••••••"
                    error={errorsReset.confirmPassword?.message}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.CONFIRM_PASSWORD_INPUT}
                    {...registerReset('confirmPassword')}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    isLoading={resetPasswordMutation.isPending}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.RESET_SUBMIT_BUTTON}
                  >
                    Actualizar Contraseña
                  </Button>

                  <Button
                    variant="ghost"
                    size="lg"
                    className="w-full"
                    onClick={() => setStep('email')}
                    leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.BACK_BUTTON}
                  >
                    Volver
                  </Button>
                </form>
              </>
            )}

            {step === 'success' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                  ¡Contraseña Actualizada!
                </h2>
                <Alert variant="success" title="Éxito" data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.SUCCESS_ALERT}>
                  Tu contraseña ha sido actualizada exitosamente. Redirigiendo al login...
                </Alert>
              </div>
            )}
          </CardBody>
        </Card>

        <p className="text-center text-sm text-secondary-600 dark:text-secondary-400 mt-6">
          © {new Date().getFullYear()} FacturaLibre. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
